import { extractDepFields } from "../components/Form/FormDefinitionRenderer/useConditional.js";

const NODE_ID_PROP = "__id";

const CONTAINER_CHILDREN = {
  stepper: (item) => (item.steps ?? []).flatMap((step) => step.children ?? []),
  accordion: (item) => item.children ?? [],
  expandable: (item) => item.children ?? [],
};

/**
 * Stamps every node in a form definition tree with a stable-for-this-session
 * __id, so a rendered control can be linked to its entry in a tree/list view.
 * Not persisted — regenerated each time a definition is loaded for viewing.
 */
export function assignNodeIds(tree) {
  const cloned = structuredClone(tree ?? []);

  const walk = (items) => {
    (items ?? []).forEach((item) => {
      if (!item || typeof item !== "object") {
        return;
      }
      item[NODE_ID_PROP] = crypto.randomUUID();

      const getChildren = CONTAINER_CHILDREN[item.control];
      if (getChildren) {
        walk(getChildren(item));
      }
    });
  };

  walk(cloned);
  return cloned;
}

/**
 * Flattens an id-annotated tree (see assignNodeIds) into a depth-ordered
 * list for a tree/list view, preserving nesting via `depth`.
 */
export function flattenAnnotatedTree(tree, depth = 0) {
  const result = [];

  (tree ?? []).forEach((item) => {
    if (!item || typeof item !== "object") {
      return;
    }

    result.push({
      id: item[NODE_ID_PROP],
      control: item.control,
      name: item.name,
      label: item.label,
      hidden: item.hidden,
      depth,
    });

    const getChildren = CONTAINER_CHILDREN[item.control];
    if (getChildren) {
      result.push(...flattenAnnotatedTree(getChildren(item), depth + 1));
    }
  });

  return result;
}

/**
 * Maps each field name to the fields whose visibility (`hidden` condition)
 * depends on it, e.g. { fahrer_identisch_halter: [{ name, label }, ...] }.
 * Used to label gating fields in a preview so it's clear they control
 * other fields' visibility.
 */
export function buildConditionDependents(tree) {
  const dependents = new Map();

  const walk = (items) => {
    (items ?? []).forEach((item) => {
      if (!item || typeof item !== "object") {
        return;
      }

      if (item.hidden && typeof item.hidden === "object") {
        extractDepFields(item.hidden).forEach((depField) => {
          const list = dependents.get(depField) ?? [];
          list.push({ name: item.name, label: item.label });
          dependents.set(depField, list);
        });
      }

      const getChildren = CONTAINER_CHILDREN[item.control];
      if (getChildren) {
        walk(getChildren(item));
      }
    });
  };

  walk(tree);
  return dependents;
}

export { NODE_ID_PROP };
